import { useState, useCallback, useMemo, useEffect } from "react";
import {
  useFetchUserAffiliations,
  useUpdateUserAffiliations,
  useFetchAffiliationRoles,
  orgRoleEntriesToAffiliationInputs,
  affiliationsToOrgRoleEntries,
} from "@core/features/affiliations";
import { toast } from "@themes/pollinator/ui/Toast";
import { useFetchOrganizations } from "@core/features/organization";
import { useDialog } from "@platform/store";
import type { AffiliationEntry } from "../../components/AffiliationList";
import type { OptionType } from "@themes/pollinator/ui/Select";
import { Loader } from "@themes/pollinator/ui/Loader";
import { AffiliationsForm } from "./AffiliationsForm";
import isEqual from "lodash.isequal";

export type Props = {
  /** Optional callback when affiliations are saved successfully */
  onSaveSuccess?: () => void;
  /** Additional CSS classes for the container */
  className?: string;
};

/**
 * Container component for AffiliationsForm.
 * Handles data fetching, mutations, and state management.
 */
export function AffiliationsFormContainer({ onSaveSuccess, className }: Props) {
  // Dialog state for delete confirmation
  const { openDialog } = useDialog();

  // Fetch current user affiliations
  const { affiliations: serverAffiliations, isLoading: affiliationsLoading } =
    useFetchUserAffiliations();

  // Fetch organizations
  const { options: orgOptions, isLoading: orgsLoading } =
    useFetchOrganizations();

  // Fetch affiliation roles
  const { roles, isLoading: rolesLoading } = useFetchAffiliationRoles();

  // Track user-created options
  const [createdOrgs, setCreatedOrgs] = useState<OptionType[]>([]);

  // Convert roles to options format
  const roleOptions: OptionType[] = useMemo(
    () =>
      roles.map((role) => ({
        label: role.name,
        value: String(role.id),
      })),
    [roles]
  );

  // Combine loaded and created options
  const allOrgOptions = useMemo(
    () => [...orgOptions, ...createdOrgs],
    [orgOptions, createdOrgs]
  );
  // Convert server data to UI format for initial state
  const initialAffiliations = useMemo(
    () => affiliationsToOrgRoleEntries(serverAffiliations),
    [serverAffiliations]
  );

  // Local state for affiliations
  const [affiliations, setAffiliations] = useState<AffiliationEntry[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync local state when initial affiliations change (initial load or after save)
  useEffect(() => {
    if (!affiliationsLoading) {
      setAffiliations(initialAffiliations);
    }
  }, [initialAffiliations, affiliationsLoading]);

  // Update mutation
  const { updateAffiliations } = useUpdateUserAffiliations({
    onSuccess: () => {
      setIsSubmitting(false);
      setError(null);
      toast.success("Affiliations saved");
      onSaveSuccess?.();
    },
    onError: (err) => {
      setIsSubmitting(false);
      const message =
        err.message || "Failed to save affiliations. Please try again.";
      setError(message);
      toast.error(message);
    },
  });

  // Has entries with org but no role (user must complete before saving)
  const hasIncompleteEntries = useMemo(
    () => affiliations.some((a) => a.organization && !a.role),
    [affiliations]
  );

  // Check if form is dirty (has unsaved changes)
  const isDirty = useMemo(() => {
    const currentWithOrg = affiliations.filter((a) => a.organization);
    const initialWithOrg = initialAffiliations.filter((a) => a.organization);
    return !isEqual(currentWithOrg, initialWithOrg);
  }, [affiliations, initialAffiliations]);

  const handleAffiliationsChange = useCallback((value: AffiliationEntry[]) => {
    setAffiliations(value);
  }, []);

  const handleCreateOrganization = useCallback((name: string) => {
    const newOption: OptionType = {
      label: name,
      value: `new-${name.toLowerCase().replace(/\s+/g, "-")}`,
      data: { isNew: true, name },
    };
    setCreatedOrgs((prev) => {
      const exists = prev.some((opt) => opt.value === newOption.value);
      return exists ? prev : [...prev, newOption];
    });
  }, []);

  const handleSave = useCallback(() => {
    setIsSubmitting(true);
    const inputs = orgRoleEntriesToAffiliationInputs(affiliations);
    updateAffiliations(inputs);
  }, [affiliations, updateAffiliations]);

  const handleCancel = useCallback(() => {
    setAffiliations(initialAffiliations);
    // Clear created options that aren't in the initial data
    setCreatedOrgs([]);
  }, [initialAffiliations]);

  const handleDeleteEntry = useCallback(
    (index: number) => {
      const entry = affiliations[index];
      const affiliationName = entry.organization?.label ?? "this affiliation";
      const isProtected = entry.isProtected ?? false;

      openDialog("affiliationDelete", {
        isProtected,
        affiliationName,
        onConfirm: () => {
          setAffiliations((prev) => prev.filter((_, i) => i !== index));
        },
      });
    },
    [affiliations, openDialog]
  );

  // Show loader while fetching initial data
  if (affiliationsLoading) {
    return (
      <div className={className}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={className}>
      <AffiliationsForm
        affiliations={affiliations}
        onAffiliationsChange={handleAffiliationsChange}
        organizationOptions={allOrgOptions}
        roleOptions={roleOptions}
        isOptionsLoading={orgsLoading || rolesLoading}
        onCreateOrganization={handleCreateOrganization}
        onSave={handleSave}
        onCancel={handleCancel}
        isDirty={isDirty}
        isSubmitting={isSubmitting}
        error={error}
        hasIncompleteEntries={hasIncompleteEntries}
        onDeleteEntry={handleDeleteEntry}
      />
    </div>
  );
}

export default AffiliationsFormContainer;
